/**
 * Enhanced DateTimeRange Component - Balanced Modern Design
 * A modern date-time range picker with hours/minutes support.
 * Supports both vertical (for filter sidebars) and horizontal layouts.
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Switch,
    FormControlLabel,
    IconButton,
    Collapse,
    Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ClearIcon from '@mui/icons-material/Clear';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './CustomDateRange.css';
import helpers from '../assets/js/helper';

// Styled Components - Balanced Modern Design
const GlassContainer = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
    left: '4px',
    gap: 10,
    padding: '12px 14px',
    background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
    borderRadius: 10,
    border: '1px solid rgba(99, 102, 241, 0.1)',
    boxShadow: '0 2px 12px rgba(99, 102, 241, 0.06)',
    transition: 'all 0.2s ease',
}));

const DateTimeField = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
}));

const FieldLabel = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 700,
    color: '#fff',
    fontFamily: 'Cairo, sans-serif',
    minWidth: 34,
    height: 34,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    boxShadow: '0 2px 6px rgba(99, 102, 241, 0.25)',
}));

const StyledInputWrapper = styled(Box)(({ focused, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 10px',
    borderRadius: 10,
    background: disabled ? '#e2e8f0' : (focused ? 'rgba(99, 102, 241, 0.06)' : '#f1f5f9'),
    border: `1.5px solid ${focused && !disabled ? '#6366f1' : 'transparent'}`,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s ease',
    opacity: disabled ? 0.7 : 1,
    flex: 1,
    minHeight: 40,
    '&:hover': {
        background: disabled ? '#e2e8f0' : 'rgba(99, 102, 241, 0.05)',
        borderColor: disabled ? 'transparent' : 'rgba(99, 102, 241, 0.25)',
    },
}));

const TimeSelect = styled('select')(({ theme }) => ({
    appearance: 'none',
    background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
    border: '1.5px solid #a5b4fc',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: '13px',
    fontWeight: 700,
    color: '#4f46e5',
    cursor: 'pointer',
    outline: 'none',
    textAlign: 'center',
    minWidth: 48,
    transition: 'all 0.15s ease',
    '&:focus': {
        borderColor: '#6366f1',
        boxShadow: '0 0 0 2px rgba(99, 102, 241, 0.15)',
    },
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
    width: 44,
    height: 24,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 2,
        '&.Mui-checked': {
            transform: 'translateX(20px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                opacity: 1,
            },
        },
    },
    '& .MuiSwitch-thumb': {
        width: 20,
        height: 20,
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
    },
    '& .MuiSwitch-track': {
        borderRadius: 12,
        backgroundColor: '#cbd5e1',
        opacity: 1,
    },
}));

// Custom Time Input Component
const CustomTimeInput = ({ value, onChange }) => {
    const hours = value?.split(':')?.[0] || '00';
    const minutes = value?.split(':')?.[1] || '00';

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 1.25,
            borderTop: '1px solid #e2e8f0',
            background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
        }}>
            <AccessTimeIcon sx={{ color: '#6366f1', fontSize: 18 }} />
            <Typography sx={{ fontSize: '12px', color: '#64748b', fontWeight: 600, fontFamily: 'Cairo' }}>الوقت:</Typography>
            {/* RTL parent: first flex child renders on the right.
                Source order = [minutes, ':', hours] → visual order = hours (left) ':' minutes (right). */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeSelect
                    value={minutes}
                    onChange={(e) => onChange(`${hours}:${e.target.value.padStart(2, '0')}`)}
                >
                    {[...Array(60).keys()].map((minute) => (
                        <option key={minute} value={minute.toString().padStart(2, '0')}>
                            {minute.toString().padStart(2, '0')}
                        </option>
                    ))}
                </TimeSelect>
                <Typography sx={{ fontWeight: 800, color: '#6366f1', fontSize: '14px' }}>:</Typography>
                <TimeSelect
                    value={hours}
                    onChange={(e) => onChange(`${e.target.value.padStart(2, '0')}:${minutes}`)}
                >
                    {[...Array(24).keys()].map((hour) => (
                        <option key={hour} value={hour.toString().padStart(2, '0')}>
                            {hour.toString().padStart(2, '0')}
                        </option>
                    ))}
                </TimeSelect>
            </Box>
        </Box>
    );
};

// Main Component
const CustomDateRange = ({
    dateRange,
    setDateRange,
    title,
    fromTitle,
    toTitle,
    horizontal = false,
    showTitle = true,
    showAllTime = true,
    disabledFrom = false,
    disabledTo = false,
}) => {
    const [t] = useTranslation("common");
    const [fromFocused, setFromFocused] = useState(false);
    const [toFocused, setToFocused] = useState(false);

    // Create a portal container for the date picker to render outside the drawer
    useEffect(() => {
        if (!document.getElementById('datepicker-portal')) {
            const portalDiv = document.createElement('div');
            portalDiv.id = 'datepicker-portal';
            document.body.appendChild(portalDiv);
        }
    }, []);

    const isAllTime = dateRange?.all_time === 1;

    const handleDateChange = (field, value) => {
        let datetimeString = null;
        try {
            const datetime = helpers.formatDateTime(value);
            datetimeString = datetime;
        } catch (error) { }

        if (field === 'from') {
            if (dateRange?.creationDateTo && value && value > dateRange.creationDateTo) {
                value = new Date(dateRange.creationDateTo);
                try {
                    datetimeString = helpers.formatDateTime(value);
                } catch (error) { }
            }
            setDateRange({
                ...dateRange,
                creationDateFrom: value,
                creation_date_from: datetimeString
            });
        } else {
            if (dateRange?.creationDateFrom && value && value < dateRange.creationDateFrom) {
                value = new Date(dateRange.creationDateFrom);
                try {
                    datetimeString = helpers.formatDateTime(value);
                } catch (error) { }
            }
            setDateRange({
                ...dateRange,
                creationDateTo: value,
                creation_date_to: datetimeString
            });
        }
    };

    const handleToggleAllTime = (checked) => {
        setDateRange({
            ...dateRange,
            all_time: checked ? 1 : 0
        });
    };

    const handleClear = (field, e) => {
        e.stopPropagation();
        if (field === 'from') {
            setDateRange({
                ...dateRange,
                creationDateFrom: null,
                creation_date_from: null
            });
        } else {
            setDateRange({
                ...dateRange,
                creationDateTo: null,
                creation_date_to: null
            });
        }
    };

    const formatDisplayDate = (date) => {
        if (!date) return null;
        try {
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            const dateStr = `${year}-${month}-${day}`;
            const timeStr = `${hours}:${minutes}`;
            return { dateStr, timeStr };
        } catch {
            return null;
        }
    };

    const DateInputButton = React.forwardRef(({ value, onClick, placeholder, field }, ref) => {
        const displayData = formatDisplayDate(field === 'from' ? dateRange?.creationDateFrom : dateRange?.creationDateTo);
        const isFocused = field === 'from' ? fromFocused : toFocused;
        const disabled = field === 'from' ? disabledFrom : disabledTo;

        return (
            <StyledInputWrapper
                ref={ref}
                onClick={disabled ? null : onClick}
                focused={isFocused ? 1 : 0}
                disabled={disabled ? 1 : 0}
            >
                <CalendarMonthIcon sx={{ color: isFocused ? '#6366f1' : '#94a3b8', fontSize: 20 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {displayData ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.1 }}>
                            <Typography sx={{
                                fontSize: '13px',
                                fontWeight: 700,
                                color: '#1e293b',
                                fontFamily: 'Cairo, monospace',
                            }}>
                                {displayData.dateStr}
                            </Typography>
                            <Typography sx={{
                                fontSize: '11px',
                                color: '#6366f1',
                                fontWeight: 600,
                                fontFamily: 'Cairo, monospace',
                            }}>
                                {displayData.timeStr}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography sx={{
                            fontSize: '12px',
                            color: '#94a3b8',
                            fontFamily: 'Cairo',
                            fontWeight: 500
                        }}>
                            اختر التاريخ
                        </Typography>
                    )}
                </Box>
                {displayData && !disabled && (
                    <IconButton
                        size="small"
                        onClick={(e) => handleClear(field, e)}
                        sx={{
                            p: 0.5,
                            color: '#94a3b8',
                            '&:hover': {
                                color: '#ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                            }
                        }}
                    >
                        <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                )}
            </StyledInputWrapper>
        );
    });

    return (
        <GlassContainer elevation={0}>
            {showAllTime && (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pb: isAllTime ? 0 : 1,
                    borderBottom: isAllTime ? 'none' : '1px solid rgba(99, 102, 241, 0.08)'
                }}>
                    {showTitle && (
                        <Box sx={{ display: 'flex',fontFamily: 'Cairo', alignItems: 'center', gap: 1 }}>
                            <DateRangeIcon sx={{ color: '#6366f1', fontSize: 18 }} /> {title}
                        </Box>
                    )}
                    <FormControlLabel
                        control={
                            <StyledSwitch
                                checked={isAllTime}
                                onChange={(e) => handleToggleAllTime(e.target.checked)}
                            />
                        }
                        label={
                            <Typography sx={{
                                fontSize: '11px',
                                fontWeight: 700,
                                color: isAllTime ? '#6366f1' : '#64748b',
                                fontFamily: 'Cairo',
                                mr: 0.5,
                                whiteSpace: 'nowrap'
                            }}>
                                {t('GLOBAL.all_time') || 'كل الوقت'}
                            </Typography>
                        }
                        labelPlacement="start"
                        sx={{ m: 0 }}
                    />
                </Box>
            )}

            {/* Date Pickers - Collapse when all_time */}
            <Collapse in={!isAllTime} timeout={200}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: horizontal ? 'row' : 'column', 
                    gap: 1, 
                    pt: showAllTime ? 0.5 : 0,
                    alignItems: horizontal ? 'center' : 'stretch'
                }}>
                    {/* From Date */}
                    <DateTimeField>
                        <FieldLabel>{t('GLOBAL.from') || 'من'}</FieldLabel>
                        <DatePicker
                            selected={dateRange?.creationDateFrom || null}
                            onChange={(date) => handleDateChange('from', date)}
                            showTimeInput
                            dateFormat="yyyy-MM-dd HH:mm"
                            placeholderText={fromTitle || 'تاريخ البداية'}
                            customInput={<DateInputButton field="from" />}
                            customTimeInput={<CustomTimeInput />}
                            calendarClassName="datetime-pro-calendar"
                            popperProps={{ strategy: 'fixed' }}
                            popperPlacement="bottom-start"
                            portalId="datepicker-portal"
                            onCalendarOpen={() => setFromFocused(true)}
                            onCalendarClose={() => setFromFocused(false)}
                            maxDate={dateRange?.creationDateTo || null}
                            disabled={disabledFrom}
                        />
                    </DateTimeField>

                    {/* Arrow Divider - Only for vertical */}
                    {!horizontal && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25 }}>
                            <ArrowDownwardIcon sx={{ fontSize: 16, color: '#cbd5e1' }} />
                        </Box>
                    )}

                    {/* To Date */}
                    <DateTimeField>
                        <FieldLabel>{t('GLOBAL.to') || 'إلى'}</FieldLabel>
                        <DatePicker
                            selected={dateRange?.creationDateTo || null}
                            onChange={(date) => handleDateChange('to', date)}
                            showTimeInput
                            dateFormat="yyyy-MM-dd HH:mm"
                            placeholderText={toTitle || 'تاريخ النهاية'}
                            customInput={<DateInputButton field="to" />}
                            customTimeInput={<CustomTimeInput />}
                            calendarClassName="datetime-pro-calendar"
                            popperProps={{ strategy: 'fixed' }}
                            popperPlacement="bottom-start"
                            portalId="datepicker-portal"
                            onCalendarOpen={() => setToFocused(true)}
                            onCalendarClose={() => setToFocused(false)}
                            minDate={dateRange?.creationDateFrom || null}
                            disabled={disabledTo}
                        />
                    </DateTimeField>
                </Box>
            </Collapse>
        </GlassContainer>
    );
};

export default CustomDateRange;
